import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPut } from '../../lib/api';
import { inrCompact } from '../../lib/format';
import { TrendingUp, Users, Target, Award, ArrowRight, AlertCircle, Calendar, CheckCircle2, Circle, Clock } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import LeadDetailDrawer from '../../components/crm/LeadDetailDrawer';
import StaleBadge from '../../components/crm/StaleBadge';

const KpiCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color}`}>
        <Icon size={15} />
      </div>
    </div>
    <div className="font-display text-2xl font-bold text-[#0F172A]">{value}</div>
    {sub && <div className="mt-2 text-xs text-slate-600">{sub}</div>}
  </div>
);

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';

export default function CRMDashboard() {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState({ overdue: [], due_today: [], this_week: [], upcoming: [], counts: {} });
  const [activeTaskBucket, setActiveTaskBucket] = useState('overdue');
  const [openDrawer, setOpenDrawer] = useState(null);
  const [staleData, setStaleData] = useState({ stale_leads: [], stale_opportunities: [], total: 0 });

  const load = async () => {
    try {
      const [dash, taskData, stale] = await Promise.all([
        apiGet('/crm/dashboard'),
        apiGet('/crm/tasks/my'),
        apiGet('/crm/sla/stale'),
      ]);
      setData(dash);
      setTasks(taskData);
      setStaleData(stale);
      // Auto-pick first non-empty bucket
      const buckets = ['overdue', 'due_today', 'this_week', 'upcoming'];
      const firstWithItems = buckets.find(b => (taskData[b] || []).length > 0) || 'overdue';
      setActiveTaskBucket(firstWithItems);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  };

  useEffect(() => { load(); }, []);

  const completeTask = async (id) => {
    try {
      await apiPut(`/crm/tasks/${id}/complete`);
      toast.success('Task completed');
      load();
    } catch (err) {
      toast.error('Failed to complete task');
    }
  };

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading CRM dashboard…</div>;

  const { kpis, leads_by_stage, opportunities_by_stage } = data;
  const leadStageData = Object.entries(leads_by_stage || {}).map(([name, value]) => ({ name, value }));
  const oppStageData = Object.entries(opportunities_by_stage || {}).map(([name, value]) => ({ name, value }));
  const COLORS = ['#1F7A6E', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  const visibleTasks = tasks[activeTaskBucket] || [];

  const taskBuckets = [
    { key: 'overdue', label: 'Overdue', icon: AlertCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
    { key: 'due_today', label: 'Due Today', icon: Calendar, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
    { key: 'this_week', label: 'This Week', icon: Clock, color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200' },
    { key: 'upcoming', label: 'Upcoming', icon: Calendar, color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200' },
  ];

  return (
    <div>
      <div className="mb-7">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Business Development</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Sales Pipeline Command Center</h1>
        <p className="text-sm text-slate-600 mt-1">Track leads, opportunities, and revenue pipeline from a single dashboard.</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total Leads" value={kpis.total_leads} sub={`${kpis.hot_leads} hot leads`} icon={Users} color="bg-teal-50 text-teal-700" />
        <KpiCard label="Opportunities" value={kpis.total_opportunities} sub={inrCompact(kpis.pipeline_value) + ' pipeline'} icon={Target} color="bg-emerald-50 text-emerald-700" />
        <KpiCard label="Weighted Pipeline" value={inrCompact(kpis.weighted_pipeline)} sub="Expected revenue" icon={TrendingUp} color="bg-amber-50 text-amber-700" />
        <KpiCard label="Win Rate" value={`${kpis.win_rate}%`} sub={`${kpis.won_count} won / ${kpis.lost_count} lost`} icon={Award} color="bg-teal-50 text-[#1F7A6E]" />
      </div>

      {/* Stale Deals Widget */}
      {staleData.total > 0 && (
        <div className="bg-red-50/50 border border-red-200 rounded-sm mb-6 overflow-hidden" data-testid="stale-deals-widget">
          <div className="px-5 py-3 border-b border-red-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-red-700" />
              <h3 className="font-display font-bold text-base text-red-900">
                {staleData.total} Stale {staleData.total === 1 ? 'Record' : 'Records'} Breaching SLA
              </h3>
            </div>
            <Link to="/app/settings/crm-sla" className="text-[10px] font-display font-bold uppercase tracking-widest text-red-700 hover:underline">
              Configure Pipeline & SLA
            </Link>
          </div>
          <div className="divide-y divide-red-100">
            {[...staleData.stale_opportunities.slice(0, 3).map(o => ({ ...o, kind: 'opportunity', label: o.project_name })),
              ...staleData.stale_leads.slice(0, 3).map(l => ({ ...l, kind: 'lead', label: l.client }))
            ].slice(0, 5).map(item => (
              <button
                key={`${item.kind}-${item.id}`}
                onClick={() => setOpenDrawer({ type: item.kind, id: item.id })}
                className="w-full px-5 py-2.5 flex items-center justify-between hover:bg-red-50 text-left"
                data-testid={`stale-item-${item.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-white text-red-700 border border-red-200 rounded-sm font-display font-bold">
                    {item.kind}
                  </span>
                  <span className="font-semibold text-sm text-[#0F172A] truncate">{item.label}</span>
                  <span className="text-xs text-slate-600">· {item.stage}</span>
                </div>
                <StaleBadge record={item} variant="inline" />
              </button>
            ))}
            {staleData.total > 5 && (
              <Link to="/app/crm/pipeline" className="block px-5 py-2 text-center text-[11px] font-display font-bold uppercase tracking-wider text-red-700 hover:bg-red-50">
                View all {staleData.total} →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Tasks Panel (HubSpot-style) */}
      <div className="bg-white border border-slate-200 rounded-sm mb-6" data-testid="crm-tasks-panel">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">My Tasks</h3>
          <Link to="/app/crm/tasks" className="text-xs font-display font-bold uppercase tracking-widest text-[#1F7A6E] hover:underline flex items-center gap-1">
            View all tasks <ArrowRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-b border-slate-200">
          {taskBuckets.map(b => {
            const count = tasks.counts?.[b.key] || 0;
            const isActive = activeTaskBucket === b.key;
            const Icon = b.icon;
            return (
              <button
                key={b.key}
                onClick={() => setActiveTaskBucket(b.key)}
                className={`px-5 py-3 text-left border-r border-slate-200 last:border-r-0 transition-colors ${
                  isActive ? `${b.bg}` : 'hover:bg-slate-50'
                }`}
                data-testid={`task-bucket-${b.key}`}
              >
                <div className={`flex items-center gap-2 ${b.color}`}>
                  <Icon size={14} />
                  <span className="text-[10px] font-display font-bold uppercase tracking-[0.16em]">{b.label}</span>
                </div>
                <div className={`font-display text-2xl font-extrabold mt-1 ${isActive ? b.color : 'text-[#0F172A]'}`}>{count}</div>
              </button>
            );
          })}
        </div>

        <div>
          {visibleTasks.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              <CheckCircle2 size={24} className="text-emerald-500 mx-auto mb-2" />
              Nothing in this bucket. You're all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visibleTasks.slice(0, 6).map(t => (
                <li key={t.id} className="px-5 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3" data-testid={`dashboard-task-${t.id}`}>
                  <button onClick={() => completeTask(t.id)} className="text-slate-400 hover:text-[#1F7A6E]" data-testid={`dashboard-task-complete-${t.id}`}>
                    <Circle size={16} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{t.description}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3">
                      <span>Due {fmtDate(t.reminder_date)}</span>
                      {t.owner && <span>· {t.owner}</span>}
                      {t.entity_label && t.entity_type && t.entity_id && (
                        <button
                          onClick={() => setOpenDrawer({ entity_type: t.entity_type, entity_id: t.entity_id })}
                          className="text-[#1F7A6E] hover:underline font-semibold"
                        >
                          {t.entity_type}: {t.entity_label}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-4">Leads by Stage</h3>
          {leadStageData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={leadStageData}>
                <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="value" fill="#1F7A6E" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-500">No lead data yet</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-4">Opportunities by Stage</h3>
          {oppStageData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={oppStageData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} strokeWidth={0}>
                  {oppStageData.map((entry, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-500">No opportunity data yet</p>}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display font-bold text-lg mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/app/crm/leads" className="p-4 border border-slate-200 rounded-sm hover:border-[#1F7A6E] transition-colors group">
            <div className="flex items-center justify-between">
              <span className="font-display font-semibold text-sm">Manage Leads</span>
              <ArrowRight size={16} className="text-slate-400 group-hover:text-[#1F7A6E]" />
            </div>
          </Link>
          <Link to="/app/crm/pipeline" className="p-4 border border-slate-200 rounded-sm hover:border-[#1F7A6E] transition-colors group">
            <div className="flex items-center justify-between">
              <span className="font-display font-semibold text-sm">Track Opportunities</span>
              <ArrowRight size={16} className="text-slate-400 group-hover:text-[#1F7A6E]" />
            </div>
          </Link>
          <Link to="/app/crm/reports" className="p-4 border border-slate-200 rounded-sm hover:border-[#1F7A6E] transition-colors group">
            <div className="flex items-center justify-between">
              <span className="font-display font-semibold text-sm">MIS Reports</span>
              <ArrowRight size={16} className="text-slate-400 group-hover:text-[#1F7A6E]" />
            </div>
          </Link>
        </div>
      </div>

      {openDrawer && (
        <LeadDetailDrawer
          entityType={openDrawer.entity_type}
          entityId={openDrawer.entity_id}
          onClose={() => { setOpenDrawer(null); load(); }}
          onUpdated={load}
        />
      )}
    </div>
  );
}
